// M12 - TherapistPatientMotricityTab
//
// Tab "Motricidad VR" del detalle del paciente. Carga las sesiones de
// motricidad del paciente, deja elegir gameKey (cuando haya >1) y
// muestra dos paneles del registry de visualizaciones:
//   - progressionView (todas las sesiones del juego)
//   - sessionView (sesion seleccionada, por defecto la mas reciente)

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Empty, Select, Skeleton, Tag, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { useFormatos } from "../../../../i18n/formatos";
import { ReviMotricityApi } from "../../../../api/revi/reviMotricityApi";
import { getMotricityVisualization } from "../MotricityCmp/visualizations/motricityVisualRegistry";
import { ClinicalSummaryCard } from "./ClinicalSummaryCard";
import { useAuth } from "../../../../hooks";

const { Title, Text } = Typography;
const { CheckableTag } = Tag;
const api = new ReviMotricityApi();

// Los cinco estados de una sesion. El estado que no reconocemos se pinta en
// crudo a proposito: es un dato del juego, y ocultarlo dejaria al terapeuta sin
// saber que ha pasado.
const ESTADOS = ["completed", "partial", "pending", "expired", "failed"];

function etiquetaEstado(t, status) {
  return ESTADOS.includes(status)
    ? t(`motricidad.estados.${status}`)
    : status || t("motricidad.estados.desconocido");
}

export function TherapistPatientMotricityTab({ patientId, active }) {
  const { t } = useTranslation(["patient", "common"]);
  const { fechaHora } = useFormatos();

  const { accessToken } = useAuth();

  // M12.1 - catalogo de juegos cargado dinamicamente desde server.
  const [games, setGames] = useState([]);
  const [gameKey, setGameKey] = useState(null);
  const [gamesLoaded, setGamesLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Carga el catalogo cuando se activa la tab por primera vez.
  useEffect(() => {
    if (!active || !accessToken || gamesLoaded) return undefined;
    let alive = true;
    (async () => {
      try {
        const res = await api.listEnabledGames(accessToken);
        const list = res?.items || [];
        if (!alive) return;
        setGames(list);
        if (list.length > 0) setGameKey((prev) => prev || list[0].gameKey);
        setGamesLoaded(true);
      } catch (e) {
        if (alive)
          setError(
            e?.body?.message || e?.message || t("motricidad.errorCatalogo"),
          );
      }
    })();
    return () => {
      alive = false;
    };
  }, [active, accessToken, gamesLoaded, t]);

  // Carga el listado cuando se activa la tab o cambia el juego.
  const loadList = useCallback(async () => {
    if (!accessToken || !patientId || !gameKey) return;
    try {
      setLoading(true);
      setError(null);
      const res = await api.listSessions(accessToken, {
        patientId,
        gameKey,
        status: "completed", // listamos solo con datos para evitar ruido
        limit: 50,
      });
      const items = res?.items || [];
      setSessions(items);
      // por defecto seleccionamos la mas reciente
      if (items.length > 0) {
        setSelectedSessionId(items[0].id);
      } else {
        setSelectedSessionId(null);
        setDetail(null);
      }
    } catch (e) {
      setError(e?.body?.message || e?.message || t("motricidad.errorSesiones"));
    } finally {
      setLoading(false);
    }
  }, [accessToken, patientId, gameKey, t]);

  useEffect(() => {
    if (active) loadList();
  }, [active, loadList]);

  // Carga el detalle de la sesion seleccionada (incluye phases).
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!accessToken || !selectedSessionId) {
        setDetail(null);
        return;
      }
      try {
        setDetailLoading(true);
        const res = await api.getSession(accessToken, selectedSessionId);
        if (alive) setDetail(res?.session || null);
      } catch (e) {
        if (alive)
          setError(
            e?.body?.message || e?.message || t("motricidad.errorGenerico"),
          );
      } finally {
        if (alive) setDetailLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [accessToken, selectedSessionId, t]);

  const viz = useMemo(() => getMotricityVisualization(gameKey), [gameKey]);
  const SessionView = viz?.sessionView;
  const ProgressionView = viz?.progressionView;
  const activeGame = useMemo(
    () => games.find((g) => g.gameKey === gameKey) || null,
    [games, gameKey],
  );

  if (gamesLoaded && games.length === 0) {
    return <Empty description={t("motricidad.sinJuegos")} />;
  }

  return (
    <div className="revi-patient-motricity-tab" style={{ padding: 8 }}>
      {/* Selector de juego/escena en "chips" que se reparten en varias filas
          (flex-wrap): nunca se salen del modal y escalan a muchas escenas sin
          barra de scroll. La activa queda resaltada. El selector de sesion
          va debajo. */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {games.map((g) => {
            const checked = gameKey === g.gameKey;
            return (
              <CheckableTag
                key={g.gameKey}
                checked={checked}
                onChange={() => setGameKey(g.gameKey)}
                style={{
                  margin: 0,
                  padding: "5px 14px",
                  borderRadius: 999,
                  fontSize: 13,
                  lineHeight: 1.5,
                  border: checked
                    ? "1px solid transparent"
                    : "1px solid #d9d9d9",
                  background: checked ? undefined : "#fff",
                  userSelect: "none",
                }}
              >
                {g.label}
              </CheckableTag>
            );
          })}
        </div>
        {sessions.length > 0 ? (
          <div style={{ marginTop: 12 }}>
            <Select
              style={{ minWidth: 280, maxWidth: "100%" }}
              value={selectedSessionId}
              onChange={setSelectedSessionId}
              options={sessions.map((s) => ({
                value: s.id,
                label: t("motricidad.etiquetaSesion", {
                  fecha: fechaHora(s.startedAt || s.createdAt),
                  estado: etiquetaEstado(t, s.status),
                }),
              }))}
            />
          </div>
        ) : null}
      </div>

      {error ? (
        <Alert
          type="error"
          showIcon
          message={t("motricidad.errorTitulo")}
          description={error}
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      ) : null}

      {loading ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : sessions.length === 0 ? (
        <Empty
          description={t("motricidad.sinSesionesDelJuego", {
            juego: activeGame?.label || gameKey,
          })}
        />
      ) : (
        <>
          <Title level={5} style={{ marginTop: 0 }}>
            {t("motricidad.sesionSeleccionada")}
          </Title>
          {detailLoading ? (
            <Skeleton active paragraph={{ rows: 4 }} />
          ) : SessionView ? (
            <SessionView session={detail} game={activeGame} />
          ) : (
            <Text type="secondary">
              {t("motricidad.sinVistaDetallada", { juego: gameKey })}
            </Text>
          )}

          {/* Parrafo redactado por IA sobre los datos de ESTA sesion. Va
              debajo de la vista de la sesion a proposito: primero los numeros,
              despues el texto que los describe. Al reves, el texto se leeria
              como la fuente y los numeros como el detalle. */}
          <ClinicalSummaryCard sessionId={selectedSessionId} />

          <Title level={5} style={{ marginTop: 24 }}>
            {t("motricidad.evolucion")}
          </Title>
          {ProgressionView ? (
            <ProgressionView sessions={sessions} game={activeGame} />
          ) : (
            <Text type="secondary">{t("motricidad.sinProgresion")}</Text>
          )}
        </>
      )}
    </div>
  );
}
